import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { creditBalance } from '../../api/billing';
import { ApiError } from '../../api/client';
import { useBuildRun } from '../../state/buildRun';
import { useDraft } from '../../state/draft';
import { clearDraftStash, restoreDraft } from '../../state/draftStash';
import {
  buildsIncluded,
  checkoutChoice,
  checkoutOffered,
  forgetTrialCheckout,
  rememberTrialCheckout,
  reportStartTrial,
  TRIAL_DAYS,
} from '../../state/trial';
import { PLANS, foundingFor, foundingPrice, longDate, priceLine, rememberedFounding, rememberedOfferTerms, REMINDER_PROMISE } from '../../state/plans';
import { useSession } from '../../state/useSession';
import { onConsent } from '../../shell/consentEvents';
import { useFunnelStep } from '../../analytics/funnel';
import { cardLater } from '../../analytics/experiment';
import { Button, Screen } from '../../ui';

/**
 * Where Stripe Checkout sends someone back to (infra/lambda/handlers/stripe.ts, for
 * checkouts started here). Before these existed, a paying customer landed on an S3 XML
 * error — charged, granted, and told nothing (ISSUES-TABLE #22).
 *
 * These pages grant nothing. The webhook owns entitlement, so a page that is never
 * reached, or reached twice, cannot change what anyone has paid for.
 *
 * A subscription checkout is a plan's 14-day trial while the account can have one, the card
 * taken up front (2026-10-27). So the page greets a trial — nothing has been charged — says when
 * the card will be, repeats the reminder promise, and reports StartTrial to the ad pixel in place
 * of the Subscribe it sent before: a trial is not yet a paying subscriber, and the trial
 * converting is the server's to report.
 */

/** How often the page asks whether the webhook has granted the trial yet. */
export const RESUME_POLL_MS = 2_000;
/**
 * How long it keeps asking before it says the account has not caught up. Stripe's webhook
 * normally lands within seconds of the redirect; past this, waiting longer in silence is worse
 * than saying so and offering the two ways on.
 */
export const RESUME_WAIT_MS = 45_000;

/**
 * What began, in words. The trial — never described as a payment — or, for an account that had had
 * its trial (one per account, 2026-09-29), the plan from day one, which is one: "nothing is charged
 * for 14 days" after a charge today is the thing the offer was split to stop saying.
 */
interface Began {
  kind: 'trial' | 'subscription' | 'plan';
  title: string;
  started: string;
  /** What the account is waiting to show: "the trial" or "the subscription". */
  thing: string;
  /** A trial begun without a card (`trial_card` b): Stripe confirmed no card. */
  noCard?: boolean;
}

/*
 * The trial's words name the plan's price and the day the card is charged when this tab chose it
 * (state/trial.ts `checkoutChoice`); without that record, what is true of every plan.
 */
function trialBegan(): Began {
  const choice = checkoutChoice();
  const plan = choice ? PLANS[choice.plan] : null;
  const date = choice?.chargeAt ? longDate(choice.chargeAt) : '';
  // With the founding offer, when this tab chose it with one (2026-10-29): the discounted first charge.
  const founding = plan ? foundingFor(plan, rememberedFounding()) : null;
  // The no-card half (analytics/experiment.ts `trial_card`, 2026-11-01): no card was taken, so none is charged.
  if (cardLater()) {
    return {
      kind: 'trial',
      title: 'Your free trial has started',
      started: `Nothing is charged.${date ? ` Your trial runs until ${date}.` : ''} To keep your site live after that, add a card from your account. ${REMINDER_PROMISE}`,
      thing: 'trial',
      noCard: true,
    };
  }
  const charged = plan && date
    ? founding
      ? `Your card is charged ${foundingPrice(plan, founding)} on ${date} unless you cancel (${founding.percentOff}% off for ${founding.months} months).`
      : `Your card is charged $${plan.amount} on ${date} unless you cancel.`
    : `Nothing is charged for ${TRIAL_DAYS} days, and then your plan renews unless you cancel.`;
  return {
    kind: 'trial',
    title: 'Your free trial has started',
    started: `${charged} ${REMINDER_PROMISE}`,
    thing: 'trial',
  };
}

function subscriptionBegan(): Began {
  const choice = checkoutChoice();
  const plan = choice ? PLANS[choice.plan] : null;
  return {
    kind: 'subscription',
    title: 'Your subscription has started',
    // A price made for the account (Credits, 2026-11-01) says its own terms.
    started: rememberedOfferTerms() ?? (plan ? `${plan.name}: ${priceLine(plan)}, until you cancel.` : 'Your subscription is active until you cancel.'),
    thing: 'subscription',
  };
}

/**
 * When this page cannot tell which it was (F23, 2026-09-30): no record of the checkout this tab
 * left for, and an account that shows no trial still on offer — either it had its trial before, or
 * the webhook recording this one has already landed. True of both, and it claims neither.
 */
const PLAN_BEGAN: Began = {
  kind: 'plan',
  title: 'Your plan has started',
  started: 'Your subscription is active until you cancel. Its terms, and the way to cancel, are on Account.',
  thing: 'subscription',
};

/** What the record of this tab's checkout says began, or null when there is no record. */
function beganFromRecord(): Began | null {
  const offered = checkoutOffered();
  if (offered === 'subscribe') return subscriptionBegan();
  if (offered === 'trial') return trialBegan();
  return null;
}

export function BillingSuccess() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  /*
   * A top-up comes back here too, marked by the server (`&kind=topup`, stripe.ts). It is not a
   * subscription: reporting it to the ad pixel as a trial would count every top-up as a new one,
   * and "Your free trial has started" would greet somebody who already had a plan.
   */
  const topup = params.get('kind') === 'topup';
  /** The trial taken at the Plan screen's Build button: back here to BUILD (`resume=build`). */
  const resume = !topup && params.get('resume') === 'build';
  /*
   * Back from Stripe with a card: the funnel's step for it (analytics/funnel.ts), whatever the
   * offer turns out to have been. A top-up is not the journey, so it is not one.
   */
  useFunnelStep('checkout_returned', !topup, { outcome: 'success' });
  const { user, isInitializing } = useSession();
  const ownerId = user?.id ?? null;
  /*
   * Which offer the checkout this tab left for was — Stripe's return address cannot say, so Plan and
   * Credits write it down right before the browser goes (state/trial.ts).
   *
   * With no record (a checkout begun in another tab, or storage blocked) this page used to assume
   * the trial, and reported StartTrial even for an account that had had its one trial and had just
   * been charged for its plan (F23, 2026-09-30). /credits is asked instead. A trial still on offer means the
   * webhook has not landed and this checkout was the trial, since the server sells the week to every
   * account that has not had it — absent reads the same, as it does everywhere (offerFor). Anything
   * else, and a question nobody can answer (signed out, offline), claims neither.
   */
  const [began, setBegan] = useState<Began | null>(beganFromRecord);
  useEffect(() => {
    if (began || topup || isInitializing) return;
    if (!ownerId) {
      setBegan(PLAN_BEGAN);
      return;
    }
    let live = true;
    void creditBalance()
      .then((b) => {
        if (live) setBegan(b.trialAvailable === false ? PLAN_BEGAN : trialBegan());
      })
      .catch(() => {
        if (live) setBegan(PLAN_BEGAN);
      });
    return () => {
      live = false;
    };
  }, [began, topup, isInitializing, ownerId]);

  /*
   * Once per checkout, not per page load, and never for a typed URL (state/trial.ts) — and only for
   * a checkout known to have been the trial. Again on a yes given on this page after it loaded: the
   * banner is often answered here, and on load the pixel sent nothing (F23). Safe to repeat, because
   * the reporter records only an event that went.
   */
  const isTrial = !topup && began?.kind === 'trial';
  useEffect(() => {
    if (!isTrial) return undefined;
    reportStartTrial(sessionId);
    return onConsent((choice) => {
      if (choice === 'granted') reportStartTrial(sessionId);
    });
  }, [sessionId, isTrial]);

  /*
   * Stripe has this trial, and the account may not show it for a while. Remembered, so that in the
   * meantime neither the Plan screen nor Credits sells it a second time (state/trial.ts). Not for a
   * checkout whose build this tab already began: the account had shown the trial by then.
   */
  useEffect(() => {
    if (!topup && ownerId && !startedHere(sessionId)) rememberTrialCheckout(ownerId, sessionId);
  }, [sessionId, topup, ownerId]);

  if (topup) {
    return (
      <Screen title="Credits on their way">
        <p className="type-body">
          Thanks — your payment went through. Your credits can take a minute to show up.
        </p>
        <Button title="See my credits" onPress={() => navigate('/credits')} />
      </Screen>
    );
  }

  if (!began) {
    return (
      <Screen title="Back from checkout">
        <p role="status" className="type-body">Checking your account…</p>
      </Screen>
    );
  }

  if (resume) return <ResumeBuild sessionId={sessionId} began={began} />;

  return (
    <Screen title={began.title}>
      <p className="type-body">{began.started} Your credits can take a minute to show up.</p>
      <Button title="Build an app" onPress={() => navigate('/new')} />
      <Button title="See my credits" variant="ghost" onPress={() => navigate('/credits')} />
    </Screen>
  );
}

type Phase =
  /** Putting the plan back, then asking /credits whether the trial is there yet. */
  | { kind: 'waiting' }
  /** Asked for long enough: the account has not caught up with Stripe. */
  | { kind: 'late' }
  /** Nothing was put away for this account in this tab. */
  | { kind: 'no-plan' }
  /** The trial is there, and some attachments could not come back: asked, not assumed. */
  | { kind: 'lost'; lost: string[] }
  /** The session has ended: every read is refused, and waiting cannot change that. */
  | { kind: 'signin' }
  | { kind: 'started' };

/*
 * Per tab and per checkout: a reload of this page after the build began must not begin another.
 * The build itself survives a reload through its own record (state/buildIntent.ts).
 */
const startedKey = (sessionId: string | null) => `saddle.resumeStarted:${sessionId ?? ''}`;
function startedHere(sessionId: string | null): boolean {
  try {
    return sessionStorage.getItem(startedKey(sessionId)) === '1';
  } catch {
    return false;
  }
}
function markStarted(sessionId: string | null): void {
  try {
    sessionStorage.setItem(startedKey(sessionId), '1');
  } catch {
    /* the build's own record still stops a second charge for the same choices */
  }
}

/**
 * Back from the trial checkout to build the app that was planned.
 *
 * Stripe redirects the moment the card is taken; the webhook that grants the trial can arrive a
 * few seconds later. A build started before it would be refused for credits the person has just
 * been promised, so this puts the plan back (state/draftStash.ts), waits — saying so — until
 * /credits shows the trial, and then starts the build exactly as the Build button does. It gives
 * up waiting after RESUME_WAIT_MS and says what is true, with the plan kept.
 */
function ResumeBuild({ sessionId, began }: { sessionId: string | null; began: Began }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isInitializing } = useSession();
  const [phase, setPhase] = useState<Phase>(() => (startedHere(sessionId) ? { kind: 'started' } : { kind: 'waiting' }));
  /** Bumped by "Check again", which starts the wait over. */
  const [attempt, setAttempt] = useState(0);
  const ownerId = user?.id ?? null;
  const building = useRef(false);

  function build(id: string): void {
    if (building.current) return;
    building.current = true;
    markStarted(sessionId);
    forgetTrialCheckout();
    setPhase({ kind: 'started' });
    // The draft is in memory now, and the build writes its own record before it asks.
    void clearDraftStash();
    void useBuildRun.getState().start(id);
  }

  useEffect(() => {
    if (isInitializing || !ownerId || startedHere(sessionId)) return;
    let live = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    setPhase({ kind: 'waiting' });
    void (async () => {
      const restored = await restoreDraft(ownerId);
      if (!live) return;
      if (!restored) {
        setPhase({ kind: 'no-plan' });
        return;
      }
      const giveUpAt = Date.now() + RESUME_WAIT_MS;
      const ask = async (): Promise<void> => {
        /*
         * A failed read is the moment, not the answer: asked again until the wait is over. Except
         * a refused session, which is a fact about the person's sign-in — waited out, it would end
         * on "your account hasn't caught up", which is not what is wrong.
         */
        let refused = false;
        const balance = await creditBalance().catch((e: unknown) => {
          refused = e instanceof ApiError && e.kind === 'unauthorized';
          return null;
        });
        if (!live) return;
        if (refused) {
          setPhase({ kind: 'signin' });
          return;
        }
        if (balance && buildsIncluded(balance)) {
          if (restored.lost.length) setPhase({ kind: 'lost', lost: restored.lost });
          else build(ownerId);
          return;
        }
        if (Date.now() >= giveUpAt) {
          setPhase({ kind: 'late' });
          return;
        }
        timer = setTimeout(() => void ask(), RESUME_POLL_MS);
      };
      await ask();
    })();
    return () => {
      live = false;
      clearTimeout(timer);
    };
    // `attempt` restarts the whole wait; `build` is stable in what it does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitializing, ownerId, sessionId, attempt]);

  /** Back to the plan, on the step they need: the draft is already in memory. */
  function backToPlan(step: 'review' | 'content'): void {
    useDraft.getState().setPlanStep(step);
    navigate('/new/plan');
  }

  if (isInitializing) {
    return (
      <Screen title={began.title}>
        <p role="status" className="type-body">Checking your account…</p>
      </Screen>
    );
  }

  if (!ownerId || phase.kind === 'signin') {
    return (
      <Screen title={began.title}>
        <p className="type-body">
          {began.started} Sign in to build your app — the plan you made is kept in this tab.
        </p>
        <Button
          title="Sign in"
          onPress={() => navigate('/sign-in', { state: { from: location.pathname + location.search } })}
        />
      </Screen>
    );
  }

  switch (phase.kind) {
    case 'started':
      return (
        <Screen title="Building your app">
          <p role="status" className="type-body">
            Your {began.thing} is active and your app is being built. It opens here when it&rsquo;s ready,
            and it will be in My Apps.
          </p>
          {began.kind === 'trial' && <p className="type-caption plan-detail">{began.started}</p>}
          <Button title="Go to My Apps" variant="ghost" onPress={() => navigate('/my-apps')} />
        </Screen>
      );
    case 'no-plan':
      return (
        <Screen title={began.title}>
          <p className="type-body">{began.started}</p>
          <p role="status" className="type-body">
            The plan you were making isn&rsquo;t in this tab any more, so describe your app again to
            build it.
          </p>
          <Button title="Describe your app" onPress={() => navigate('/new/idea')} />
        </Screen>
      );
    case 'late':
      return (
        <Screen title={began.title}>
          <p role="status" className="type-body">
            Stripe has your {began.thing}, but your account hasn&rsquo;t caught up with it yet, so the build
            hasn&rsquo;t started. Your plan is saved.
          </p>
          <Button title="Check again" onPress={() => setAttempt((a) => a + 1)} />
          <Button title="Back to your plan" variant="ghost" onPress={() => backToPlan('review')} />
        </Screen>
      );
    case 'lost': {
      const them = phase.lost.length === 1 ? 'it' : 'them';
      return (
        <Screen title={began.title}>
          <p role="status" className="type-body">
            {phase.lost.join(', ')} couldn&rsquo;t be kept through checkout in this browser. Add{' '}
            {them} again before building, or build without {them}.
          </p>
          <Button title={`Add ${them} again`} onPress={() => backToPlan('content')} />
          <Button title={`Build without ${them}`} variant="ghost" onPress={() => build(ownerId)} />
        </Screen>
      );
    }
    case 'waiting':
    default:
      return (
        <Screen title={began.title}>
          <p role="status" className="type-body">
            {began.noCard ? 'Your trial is confirmed.' : 'Stripe has confirmed your card.'} We&rsquo;re waiting for your account to show the {began.thing} —
            this usually takes a few seconds — and then your app starts building.
          </p>
          {/* The trial's terms and the reminder promise, said while it waits (owner, 2026-10-27). */}
          {began.kind === 'trial' && <p className="type-caption plan-detail">{began.started}</p>}
        </Screen>
      );
  }
}

export function BillingCancelled() {
  const navigate = useNavigate();
  return (
    <Screen title="No charge made">
      <p className="type-body">You left checkout before paying, so nothing was charged.</p>
      <Button title="Back to credits" onPress={() => navigate('/credits')} />
    </Screen>
  );
}
